/**
 * Container for the Promote action modal.
 *
 * Manages annual salary and contract duration draft state.
 * On confirm, delegates to the parent callback with salary and duration.
 * Supports edit mode with a "Cancel Promote" action.
 * @module features/squad-planning/components/PromoteActionModal
 */

import { type Href, useRouter } from "expo-router";
import React, { memo, useCallback, useMemo, useState } from "react";

import { POSTHOG_FLAGS } from "@/config/featureFlags";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";
import { buildComplianceHref } from "@/features/financial-compliance/navigation/complianceRoute";

import { useLiveImpact } from "../../../hooks/useLiveImpact";

import PromoteActionModalView from "./PromoteActionModalView";

import type { ActionTerms } from "@/features/financial-compliance/domain/actionFinancials";

/** Props for {@link PromoteActionModalContainer} */
export interface PromoteActionModalProps {
  /** Whether the modal is open */
  readonly isOpen: boolean;
  /** Called to close the modal */
  readonly onClose: () => void;
  /** Called with annual salary and duration on confirm */
  readonly onConfirm: (
    annualSalary: number | null,
    durationMonths: number | null
  ) => void;
  /** Called to remove the action (edit mode only) */
  readonly onCancelAction?: () => void;
  /** Player name for the title */
  readonly playerName: string;
  /**
   * Player the action applies to.
   *
   * Optional so no mount point breaks by omitting it; the Live Impact verdict
   * uses it to avoid double-counting a player who is already staged.
   */
  readonly playerId?: string | null;
  /** Initial annual salary value (for edit mode) */
  readonly initialAnnualSalary?: number | null;
  /** Initial duration in months (for edit mode) */
  readonly initialDuration?: number | null;
  /** Whether the modal is in edit mode (existing action) */
  readonly isEditMode?: boolean;
}

/**
 * Container managing Promote action modal state.
 * @param root0 - Component props
 * @param root0.isOpen - Whether modal is visible
 * @param root0.onClose - Close handler
 * @param root0.onConfirm - Confirm handler with salary and duration
 * @param root0.onCancelAction - Remove action handler (edit mode)
 * @param root0.playerName - Player name for display
 * @param root0.playerId - Player the action applies to
 * @param root0.initialAnnualSalary - Initial salary for edit mode
 * @param root0.initialDuration - Initial duration for edit mode
 * @param root0.isEditMode - Whether in edit mode
 */
const PromoteActionModalContainer = ({
  isOpen,
  onClose,
  onConfirm,
  onCancelAction,
  playerName,
  playerId = null,
  initialAnnualSalary = null,
  initialDuration = null,
  isEditMode = false,
}: PromoteActionModalProps) => {
  const [annualSalary, setAnnualSalary] = useState<number | null>(
    initialAnnualSalary
  );
  const [durationText, setDurationText] = useState(
    initialDuration != null ? String(initialDuration) : ""
  );

  const router = useRouter();

  const terms = useMemo<ActionTerms>(
    () => ({
      kind: "Promote",
      annualSalary,
      durationMonths: durationText !== "" ? Number(durationText) : null,
    }),
    [annualSalary, durationText]
  );
  const { model: liveImpact, scenarioId } = useLiveImpact(terms, playerId);
  // SE-8003: the Compliance tab is unreachable with the flag off, so this
  // link must not navigate into it. `=== true` is the fail-safe idiom — an
  // unconfigured flag, a still-loading PostHog client, or any non-`true`
  // value all skip the navigation, never perform it.
  const isComplianceEnabled =
    usePostHogFeatureFlag(POSTHOG_FLAGS.SQUAD_FINANCIALS_TAB) === true;

  const handleViewFullImpact = useCallback(() => {
    onClose();
    if (isComplianceEnabled) {
      router.push(buildComplianceHref(scenarioId) as Href);
    }
  }, [router, scenarioId, onClose, isComplianceEnabled]);

  const handleAnnualSalaryChange = useCallback((value: number | null) => {
    setAnnualSalary(value);
  }, []);

  const handleDurationChange = useCallback((text: string) => {
    setDurationText(text.replace(/\D/g, ""));
  }, []);

  const handleConfirm = useCallback(() => {
    const months = durationText !== "" ? parseInt(durationText, 10) : null;
    onConfirm(annualSalary, Number.isNaN(months) ? null : months);
  }, [annualSalary, durationText, onConfirm]);

  if (!isOpen) return null;

  return (
    <PromoteActionModalView
      isOpen={isOpen}
      playerName={playerName}
      annualSalary={annualSalary}
      durationText={durationText}
      onAnnualSalaryChange={handleAnnualSalaryChange}
      onDurationChange={handleDurationChange}
      onConfirm={handleConfirm}
      onCancel={onClose}
      onCancelAction={onCancelAction}
      isEditMode={isEditMode}
      liveImpact={liveImpact}
      onViewFullImpact={handleViewFullImpact}
    />
  );
};

PromoteActionModalContainer.displayName = "PromoteActionModalContainer";

export default memo(PromoteActionModalContainer);
